import { useState } from "react";
import { Turnstile } from "react-turnstile";
import { Mail, Globe, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Reveal } from "@/components/Reveal";
import { useSite } from "@/context/SiteContext";

const FIELD =
  "rounded-none border-black/20 bg-white focus-visible:ring-0 focus-visible:border-gavra h-12 text-base";

export default function Contact() {
  const site = useSite();
  const c = site.contact || {};
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    if (!turnstileToken) {
      toast.error("Please complete the security verification.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", {
        name: form.name,
        email: form.email,
        subject: form.subject || "Website Inquiry",
        message: form.message,
        turnstile_token: turnstileToken,
      });
      toast.success("Message sent!", { description: "We'll get back to you shortly." });
      setForm({ name: "", email: "", subject: "", message: "" });
      setTurnstileToken("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send. Try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen bg-white pt-20">
      <section className="mx-auto max-w-[1500px] px-5 md:px-10 py-16 md:py-24">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-2">
          {/* left */}
          <div>
            <Reveal>
              <p className="font-mono-hw text-[12px] uppercase tracking-[0.3em] text-gavra">{c.eyebrow}</p>
              <h1 className="mt-4 font-heading text-6xl font-black uppercase leading-[0.82] tracking-tighter md:text-8xl">
                {(c.title || "Get In\nTouch").split("\n").map((ln, i) => (
                  <span key={i} className="block">{ln}</span>
                ))}
              </h1>
              <p className="mt-8 max-w-md text-base leading-relaxed text-black/60 md:text-lg">
                {c.description}
              </p>

              <div className="mt-12 space-y-6">
                {[
                  { icon: Mail, label: "Email", value: c.email },
                  { icon: Globe, label: "Web", value: c.web },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-4 border-b border-black/10 pb-5">
                    <item.icon className="h-5 w-5 text-gavra" strokeWidth={2.5} />
                    <div>
                      <p className="font-mono-hw text-[10px] uppercase tracking-widest text-black/40">{item.label}</p>
                      <p className="font-heading text-lg font-bold">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          {/* form */}
          <Reveal delay={0.1}>
            <form data-testid="contact-form" onSubmit={submit} className="border border-black/10 p-6 md:p-10">
              <div className="space-y-6">
                <div>
                  <label className="font-mono-hw text-[11px] uppercase tracking-widest text-black/50">Name *</label>
                  <Input data-testid="contact-name" value={form.name} onChange={set("name")} placeholder="Your name" className={`mt-2 ${FIELD}`} />
                </div>
                <div>
                  <label className="font-mono-hw text-[11px] uppercase tracking-widest text-black/50">Email *</label>
                  <Input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={`mt-2 ${FIELD}`} />
                </div>
                <div>
                  <label className="font-mono-hw text-[11px] uppercase tracking-widest text-black/50">Subject</label>
                  <Input data-testid="contact-subject" value={form.subject} onChange={set("subject")} placeholder="What's this about?" className={`mt-2 ${FIELD}`} />
                </div>
                <div>
                  <label className="font-mono-hw text-[11px] uppercase tracking-widest text-black/50">Message *</label>
                  <Textarea data-testid="contact-message" value={form.message} onChange={set("message")} placeholder="Tell us more…" rows={6} className="mt-2 rounded-none border-black/20 bg-white focus-visible:ring-0 focus-visible:border-gavra text-base" />
                </div>
                <div className="flex justify-center">
                  <Turnstile
                  sitekey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                  onVerify={(token) => setTurnstileToken(token)}
                  onExpire={() => setTurnstileToken("")}
                  onError={() => setTurnstileToken("")}
                  />
                </div>
                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={sending}
                  className="flex w-full items-center justify-center gap-3 bg-black px-8 py-4 text-[13px] font-bold uppercase tracking-[0.15em] text-white transition-colors hover:bg-gavra disabled:opacity-50"
                >
                  {sending ? "Sending…" : (<>Send message <Send className="h-4 w-4" /></>)}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
